/**
 * ProgramMarketView — Market ▸ Market Search, scoped to a specific program.
 *
 * Candidate retrieval is technology/family-driven. Products are scored against
 * the program's scorecards (Basic Requirements + Technical Performance + the
 * standard three) via UnifiedScoringService. Selecting a solution-family pill
 * scopes results to that family's technology subtree.
 *
 * This is a results-browsing surface only: the old left scorecard-adjustment
 * sidebar (Requirements filters + per-family technical scorecard + draft
 * promotion) has been removed — that work now lives on the Solutions tab. The
 * omnipresent agent pane is rendered globally and AppShell reserves its column,
 * so this page does not mount it.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Search, ArrowUpDown, ChevronDown, AlertTriangle, ArrowRight, RotateCcw } from 'lucide-react'
import Button from '../../components/ui/Button'
import Modal from '../../components/ui/Modal'
import {
  acceptedOf,
  resolveEmptyState,
  type SolutionFamily,
  type MarketEmptyState,
} from './programMarketEmptyState'
import { ROUTES } from '../../lib/routing'
import { useViewportContext } from '../../hooks/useViewportContext'
import {
  ProductResultCard,
  ViewToggle,
  TradeoffsView,
  MapView,
} from '../../components/marketview'
import type { MarketViewTab } from '../../components/marketview/ViewToggle'
import {
  api,
  MarketViewSearchResult,
} from '../../services/api'
import { tokenManager } from '../../lib/api'
import { useHeaderDensity } from '../../contexts/HeaderDensityContext'
import { agentOverallValue, agentScoreValue } from '../../lib/scoreDisplay'
import VendorDetailOverlay from '../../components/VendorDetailOverlay'
import { trackEvent } from '../../services/tracking'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

type SortOption = 'overall' | 'need_fit' | 'technical' | 'manufacturing' | 'vc_metrics' | 'dod_partnership'

// Per-family research triage, sourced from
// GET /api/programs/{id}/family-technologies-needing-research/. Kept as
// worst-of across a family's linked technologies.
type FamilyResearchStatus = 'fresh' | 'in_progress' | 'needs_research'
const STATUS_RANK: Record<FamilyResearchStatus, number> = {
  fresh: 0,
  in_progress: 1,
  needs_research: 2,
}
function worstStatus(a: FamilyResearchStatus, b: FamilyResearchStatus): FamilyResearchStatus {
  return STATUS_RANK[a] >= STATUS_RANK[b] ? a : b
}
const RESEARCH_POLL_MS = 20_000

const ALL_SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'overall', label: 'Overall Score' },
  { value: 'need_fit', label: 'Basic Requirements' },
  { value: 'technical', label: 'Technical Performance' },
  { value: 'manufacturing', label: 'Production Readiness' },
  { value: 'vc_metrics', label: 'Company Stability' },
  { value: 'dod_partnership', label: 'DoW Partnership' },
]

// Family-driven search excludes the program-level Basic Requirements sort
const FAMILY_SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'overall', label: 'Overall Score' },
  { value: 'technical', label: 'Technical Performance' },
  { value: 'manufacturing', label: 'Production Readiness' },
  { value: 'vc_metrics', label: 'Company Stability' },
  { value: 'dod_partnership', label: 'DoW Partnership' },
]

/**
 * Explains a blank MarketView.
 *
 * Each branch has to survive one test: would a user in this exact situation
 * read it as an accurate account of what happened? "Select a solution family"
 * fails that test for anyone who already selected one, so it is shown only when
 * genuinely nothing has been accepted. The unanchored branches say plainly that
 * the fault is ours - a user cannot be expected to know what an anchor
 * technology is, only that this is not their mistake to fix by trying harder.
 */
const MarketViewEmptyState: React.FC<{
  state: MarketEmptyState
  solutionsPath: string
  /**
   * The page's own research button, repeated here so a user looking at an
   * empty list does not have to find the pill row to fix it. Only the
   * `no-products` branch takes it: every other branch has a different missing
   * step (accept a family, repair an anchor) that research would not solve.
   */
  researchAction?: React.ReactNode
}> = ({ state, solutionsPath, researchAction }) => {
  // Cause not established yet. A wrong explanation is worse than none.
  if (state.kind === 'pending') return null

  const solutionsLink = (label: string) => (
    <Link to={solutionsPath}>
      <Button variant="outline" size="sm">
        {label}
        <ArrowRight className="w-3.5 h-3.5" />
      </Button>
    </Link>
  )

  if (state.kind === 'no-families') {
    return (
      <div className="text-center max-w-md">
        <Search className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
        <p className="text-foreground mb-2">No solution families yet.</p>
        <p className="text-sm text-muted-foreground mb-4">
          Research hasn’t proposed any solution families for this program, so
          there is nothing to search over. Running research on the Solutions
          page builds the candidate pool.
        </p>
        {solutionsLink('Go to Solutions')}
      </div>
    )
  }

  // The common case in ryan's user testing: research worked, families are
  // sitting there, and nobody accepted one. Naming the count makes the fix
  // concrete - the user knows there is something waiting, not that they are
  // missing a setting somewhere.
  if (state.kind === 'families-unaccepted') {
    if (state.reviewableCount === 0) {
      return (
        <div className="text-center max-w-md">
          <Search className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <p className="text-foreground mb-2">No solution families accepted.</p>
          <p className="text-sm text-muted-foreground mb-4">
            Every family proposed for this program was rejected or superseded,
            so there is nothing to search over. Re-run research on the Solutions
            page to propose new ones.
          </p>
          {solutionsLink('Go to Solutions')}
        </div>
      )
    }
    return (
      <div className="text-center max-w-md">
        <Search className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
        <p className="text-foreground mb-2">
          {state.reviewableCount === 1
            ? '1 solution family is waiting for review.'
            : `${state.reviewableCount} solution families are waiting for review.`}
        </p>
        <p className="text-sm text-muted-foreground mb-4">
          MarketView only searches families you’ve accepted. Accept at least one
          on the Solutions page to see matching products.
        </p>
        {solutionsLink(
          state.reviewableCount === 1 ? 'Review 1 family' : `Review ${state.reviewableCount} families`,
        )}
      </div>
    )
  }

  // No self-serve repair exists for this one. `POST /api/solution-families/
  // {id}/set-technology/` can fix the row, but nothing in the UI calls it, so
  // the honest next action is to run research: the deep pass inserts fresh
  // families that do carry an anchor, which gives the user a working search
  // without pretending they can patch the broken row themselves.
  if (state.kind === 'family-unanchored' || state.kind === 'all-unanchored') {
    const subject =
      state.kind === 'family-unanchored'
        ? `“${state.familyName}” has`
        : 'The accepted solution families have'
    return (
      <div className="text-center max-w-md">
        <AlertTriangle className="w-12 h-12 text-warning/60 mx-auto mb-4" />
        <p className="text-foreground mb-2">Results couldn’t be loaded.</p>
        <p className="text-sm text-muted-foreground mb-4">
          {subject} no anchor technology, so there is no product pool to search.
          This is a data problem on our side, not something you missed. Running
          research on the Solutions page proposes replacement families that do
          have one.
        </p>
        {solutionsLink('Go to Solutions')}
      </div>
    )
  }

  return (
    <div className="text-center max-w-md">
      <Search className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
      <p className="text-foreground mb-2">No products found.</p>
      <p className="text-sm text-muted-foreground mb-4">
        {state.familyName
          ? `Nothing in our catalogue is tagged under “${state.familyName}” yet.`
          : 'Nothing in our catalogue is tagged under this program’s solution families yet.'}{' '}
        {researchAction
          ? 'Running vendor research will build the list.'
          : 'Running research on the Solutions page will widen the pool.'}
      </p>
      {researchAction ? (
        <div className="flex items-center justify-center gap-3">
          {researchAction}
          {solutionsLink('Go to Solutions')}
        </div>
      ) : (
        solutionsLink('Go to Solutions')
      )}
    </div>
  )
}

interface ProgramMarketViewProps {
  /** Render bounded (fixed height, no full-page fill) for inline embedding. */
  embedded?: boolean
  /** Program id override; falls back to the route param when omitted. */
  programId?: string
  /** Fired after a star is added or removed, from either the card or the
   *  detail overlay. The Vendor Research module page uses it to re-read its
   *  confirm guard so the "Mark as Complete" hint tracks the live shortlist. */
  onShortlistChange?: () => void
}

const ProgramMarketView: React.FC<ProgramMarketViewProps> = ({
  embedded = false,
  programId: programIdProp,
  onShortlistChange,
}) => {
  const params = useParams<{ programId: string }>()
  const programId = programIdProp ?? params.programId
  const { condensed } = useHeaderDensity()

  // Program data
  const [programName, setProgramName] = useState('')
  const [scorecardCount, setScorecardCount] = useState(0)

  // Search state
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasSearched, setHasSearched] = useState(false)

  // Results
  const [products, setProducts] = useState<MarketViewSearchResult[]>([])
  const [totalResults, setTotalResults] = useState(0)

  // UI state
  const [currentView, setCurrentView] = useState<MarketViewTab>('products')
  const [sortBy, setSortBy] = useState<SortOption>('overall')
  const [showSortDropdown, setShowSortDropdown] = useState(false)

  // Vendor detail overlay
  const [selectedResult, setSelectedResult] = useState<MarketViewSearchResult | null>(null)
  const [selectedResultIndex, setSelectedResultIndex] = useState<number>(0)

  // Solution family tabs
  const [families, setFamilies] = useState<SolutionFamily[]>([])
  const [familiesLoaded, setFamiliesLoaded] = useState(false)
  // Only accepted families are searchable, so they are what the tabs offer.
  const acceptedFamilies = useMemo(() => acceptedOf(families), [families])
  const [activeFamilyId, setActiveFamilyId] = useState<string | null>(null)

  // Per-family research triage (see FamilyResearchStatus). Empty on first
  // render; populated once the family-technologies-needing-research endpoint
  // returns.
  const [familyResearchStatus, setFamilyResearchStatus] = useState<
    Record<string, FamilyResearchStatus>
  >({})
  // Products already attached to each family's technologies, from the same
  // endpoint. Decides the button's label and whether re-running needs a
  // confirmation - and it is the server's count, not a count of what this
  // page happens to have fetched.
  const [familyProductCount, setFamilyProductCount] = useState<Record<string, number>>({})

  // Research kickoff: `researchBusy` covers only the POST itself. The running
  // state the button shows comes from `familyResearchStatus`, which is
  // server-derived, so a second tab and a reload both agree with this one.
  const [researchBusy, setResearchBusy] = useState(false)
  const [confirmResearch, setConfirmResearch] = useState(false)
  const [researchError, setResearchError] = useState<string | null>(null)

  // Pagination state
  const [searchId, setSearchId] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const sortOptions = activeFamilyId ? FAMILY_SORT_OPTIONS : ALL_SORT_OPTIONS
  const solutionsPath = ROUTES.pm.programs.children!.detail.children!.solutions.build({
    programId: programId ?? '',
  })

  // Tier 2: Register viewport serializer for agent awareness
  useViewportContext('program_market', () => ({
    program_id: programId,
    program_name: programName,
    has_searched: hasSearched,
    current_view: currentView,
    sort_by: sortBy,
    total_results: totalResults,
    active_family_id: activeFamilyId,
    scorecard_count: scorecardCount,
    products: products.slice(0, 10).map(p => ({
      product_id: p.product.id,
      name: p.product.name,
      vendor: p.vendor.name,
      overall_score: agentOverallValue(p.scores),
      // 'N/A' / 'Not scored' rather than the placeholder number the agent
      // would otherwise quote as a real score (DEV-584).
      need_fit: agentScoreValue(p.scores?.need_fit),
      technical: agentScoreValue(p.scores?.technical),
    })),
  }), [programId, programName, hasSearched, currentView, sortBy, totalResults, activeFamilyId, scorecardCount, products])

  // ── Load program data ──────────────────────────────────────────

  useEffect(() => {
    const loadProgramData = async () => {
      if (!programId) return
      const token = tokenManager.getAccessToken()

      try {
        // Load program
        const progRes = await fetch(`${API_BASE_URL}/api/programs/${programId}/`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (progRes.ok) {
          const progData = await progRes.json()
          setProgramName(progData.name || '')
        }

        // Load every solution family from the active taxonomy. The unaccepted
        // ones are not searchable, but their count is what lets the empty
        // state tell "review these" apart from "research produced nothing".
        const taxRes = await fetch(
          `${API_BASE_URL}/api/solution-taxonomies/?program=${programId}&is_active=true`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        if (taxRes.ok) {
          const taxData = await taxRes.json()
          const activeTax = taxData.results?.[0] ?? (Array.isArray(taxData) ? taxData[0] : null)
          if (activeTax) {
            const all: SolutionFamily[] = (activeTax.families ?? []).map((f: any) => ({
              id: f.id,
              name: f.name,
              status: f.status,
              technology_id: f.technology_id ?? null,
              technology_name: f.technology_name ?? null,
            }))
            setFamilies(all)
          } else {
            // No active taxonomy — clear families and reset to "All" view
            setFamilies([])
            setActiveFamilyId(null)
          }
        }

        // Load scorecards just for the agent viewport count (filters live elsewhere now)
        const scRes = await fetch(`${API_BASE_URL}/api/scorecards/?program=${programId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (scRes.ok) {
          const scData = await scRes.json()
          setScorecardCount((scData.results || scData || []).length)
        }
      } catch (err) {
        console.error('Failed to load program data:', err)
      } finally {
        // Settled either way. An empty list now means "none accepted" rather
        // than "still in flight", so the empty state may name a cause.
        setFamiliesLoaded(true)
      }
    }

    loadProgramData()
  }, [programId])

  // Validate active family when accepted families change
  useEffect(() => {
    if (activeFamilyId !== null && acceptedFamilies.length > 0) {
      const isValidFamily = acceptedFamilies.some(f => f.id === activeFamilyId)
      if (!isValidFamily) {
        console.warn(`Active family ${activeFamilyId} no longer in accepted families, resetting to All`)
        setActiveFamilyId(null)
      }
    }
  }, [acceptedFamilies, activeFamilyId])

  // ── Per-family research triage ─────────────────────────────────
  //
  // Family accept auto-kicks tech_discovery ai.Jobs (programs/signals.py
  // `_kickoff_tech_research_on_accept`), skipping any technology that already
  // has products. This endpoint returns the resulting per-family status and
  // product count: it badges the pills, gates the results panel, and tells the
  // re-research button below which of its four states to render.
  const loadFamilyResearchStatus = useCallback(async () => {
    if (!programId) return
    const token = tokenManager.getAccessToken()
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/programs/${programId}/family-technologies-needing-research/`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!res.ok) return
      const data = await res.json()
      const byFamily: Record<string, FamilyResearchStatus> = {}
      // Summed, not worst-of: a family can link several technologies and the
      // question the button asks is "does re-running overwrite anything",
      // which is true if any one of them has products.
      const countByFamily: Record<string, number> = {}
      type Entry = { family_id: string; status: FamilyResearchStatus; product_count?: number }
      for (const entry of (data.entries ?? []) as Entry[]) {
        const prev = byFamily[entry.family_id]
        byFamily[entry.family_id] = prev ? worstStatus(prev, entry.status) : entry.status
        countByFamily[entry.family_id] =
          (countByFamily[entry.family_id] ?? 0) + (entry.product_count ?? 0)
      }
      setFamilyResearchStatus(byFamily)
      setFamilyProductCount(countByFamily)
    } catch (err) {
      // Best-effort — don't break the surface if the triage endpoint fails.
      console.warn('Failed to load family research status:', err)
    }
  }, [programId])

  useEffect(() => {
    loadFamilyResearchStatus()
  }, [loadFamilyResearchStatus])

  // Poll while anything is still running. Stops as soon as the map is empty
  // or every family is `fresh` / `needs_research` (the latter is a stuck
  // state — the auto-kickoff signal should have fired on accept; if it
  // didn't we don't want to hammer the endpoint).
  useEffect(() => {
    const anyInProgress = Object.values(familyResearchStatus).some(s => s === 'in_progress')
    if (!anyInProgress) return
    const timer = setInterval(loadFamilyResearchStatus, RESEARCH_POLL_MS)
    return () => clearInterval(timer)
  }, [familyResearchStatus, loadFamilyResearchStatus])

  // ── Re-research ────────────────────────────────────────────────
  //
  // The families a research click covers: the selected one, or all of them on
  // "All". Scoped to the pill row because research is per-technology, and the
  // pills are the only per-technology control on this page.
  const researchScope = useMemo(
    () => (activeFamilyId
      ? acceptedFamilies.filter(f => f.id === activeFamilyId)
      : acceptedFamilies),
    [acceptedFamilies, activeFamilyId],
  )

  // Server-derived, so a second tab agrees and a reload does not lose it.
  const researchRunning = useMemo(
    () => researchScope.some(f => familyResearchStatus[f.id] === 'in_progress'),
    [researchScope, familyResearchStatus],
  )

  const researchProductCount = useMemo(
    () => researchScope.reduce((n, f) => n + (familyProductCount[f.id] ?? 0), 0),
    [researchScope, familyProductCount],
  )

  const researchLabel = researchProductCount > 0 ? 'Re-research' : 'Research vendors'

  const runResearch = useCallback(async () => {
    // A family already running is left out: the server triages it
    // `in_progress` and skips it anyway, and re-POSTing it only muddies the
    // failure count below.
    const targets = researchScope.filter(f => familyResearchStatus[f.id] !== 'in_progress')
    if (researchBusy || targets.length === 0) return

    setResearchBusy(true)
    setResearchError(null)
    const token = tokenManager.getAccessToken()
    try {
      // `force` only matters once products exist: without it the server's
      // zero-products gate skips a technology that already has some, which is
      // correct for the automatic kickoffs and wrong for an explicit click.
      const force = researchProductCount > 0
      const results = await Promise.allSettled(
        targets.map(family =>
          fetch(`${API_BASE_URL}/api/solution-families/${family.id}/research/`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ force }),
          }).then(res => {
            if (!res.ok && res.status !== 202) throw new Error(`research failed (${res.status})`)
            return res
          }),
        ),
      )
      const failed = results.filter(r => r.status === 'rejected').length
      if (failed === targets.length) {
        setResearchError('Could not start vendor research. Please try again.')
      } else if (failed > 0) {
        setResearchError(`Research started, but ${failed} of ${targets.length} families failed to kick off.`)
      }
      if (failed < targets.length) {
        trackEvent('family_research_started', {
          program_id: programId,
          family_count: targets.length - failed,
          forced: force,
        })
        // The job puck polls on its own cycle; this is the event it already
        // listens for (useJobPoll), so the run shows up at once rather than
        // up to one poll interval later.
        window.dispatchEvent(new Event('jobStarted'))
      }
    } finally {
      setResearchBusy(false)
      // Flip the pills to `in_progress` without waiting for the next poll.
      loadFamilyResearchStatus()
    }
  }, [
    researchScope, familyResearchStatus, researchBusy, researchProductCount,
    programId, loadFamilyResearchStatus,
  ])

  // No confirmation with nothing to lose; a confirmation once products exist,
  // because the run costs 10-20 minutes of deep research.
  const onResearchClick = useCallback(() => {
    if (researchProductCount > 0) setConfirmResearch(true)
    else runResearch()
  }, [researchProductCount, runResearch])

  const researchButton = researchScope.length === 0 ? null : (
    <Button
      variant={researchProductCount > 0 ? 'outline' : 'default'}
      size="sm"
      disabled={researchRunning || researchBusy}
      onClick={onResearchClick}
      className="gap-1.5 text-xs"
      title={
        researchRunning
          ? 'Vendor research is already running for this selection.'
          : activeFamilyId
            ? undefined
            : 'Runs research for every accepted solution family.'
      }
    >
      {researchRunning || researchBusy ? (
        <>
          <span
            aria-hidden
            className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"
          />
          Researching…
        </>
      ) : (
        <>
          <RotateCcw className="w-3.5 h-3.5" />
          {researchLabel}
        </>
      )}
    </Button>
  )

  // ── Search ─────────────────────────────────────────────────────
  //
  // Retrieval is technology/family-driven — there is no free-text query on this
  // surface. Selecting a family pill scopes results to that family's technology
  // subtree; the program-level ("All") retrieval scores against all scorecards.

  const handleSearch = useCallback(async (sortOverride?: SortOption) => {
    setIsLoading(true)
    setError(null)
    setHasSearched(true)
    setSearchId(null)
    setHasMore(false)

    try {
      const searchParams = {
        program_id: programId ? parseInt(programId) : undefined,
        // Use the explicit override when provided — handleSortChange calls this
        // synchronously right after setSortBy, before the new sortBy state has
        // been applied to this closure.
        sort_by: sortOverride ?? sortBy,
        ...(activeFamilyId && { solution_family_id: activeFamilyId }),
      }
      const results = await api.marketviewSearch(searchParams)

      setProducts(results.products || [])
      setTotalResults(results.total || 0)
      setSearchId(results.search_id ?? null)
      setHasMore(results.has_more ?? false)
    } catch (err) {
      console.error('Search failed:', err)
      const errorMessage = err instanceof Error ? err.message : 'Search failed'

      // If the error is about an invalid family, reset to "All" view
      if (errorMessage.includes('SolutionFamily') && errorMessage.includes('not found')) {
        setActiveFamilyId(null)
        setError('The selected solution family is no longer available. Showing all results.')
        // Retry search without family filter
        setTimeout(() => handleSearch(), 100)
        return
      }

      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [programId, sortBy, activeFamilyId])

  // Re-search when active family changes (after initial load)
  const prevFamilyIdRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    if (prevFamilyIdRef.current === undefined) {
      prevFamilyIdRef.current = activeFamilyId
      return
    }
    if (prevFamilyIdRef.current !== activeFamilyId) {
      prevFamilyIdRef.current = activeFamilyId
      handleSearch()
    }
  }, [activeFamilyId])

  // Auto-refresh results when a family finishes researching, so the user
  // doesn't have to re-click to see freshly-scored products.
  //
  //  - "All" view: refresh whenever *any* family flips in_progress → fresh
  //    (that family's products now exist in the program-wide result set).
  //  - Specific family: refresh only when *that* family flips.
  //
  // Uses a ref snapshot so the effect can compare the previous triage against
  // the current one without re-triggering itself.
  const prevFamilyResearchStatusRef = useRef<Record<string, FamilyResearchStatus>>({})
  useEffect(() => {
    const prev = prevFamilyResearchStatusRef.current
    const flippedFresh = (id: string) =>
      prev[id] === 'in_progress' && familyResearchStatus[id] === 'fresh'
    const anyFlippedFresh = Object.keys(familyResearchStatus).some(flippedFresh)
    prevFamilyResearchStatusRef.current = familyResearchStatus

    if (!hasSearched || !anyFlippedFresh) return
    if (activeFamilyId === null || flippedFresh(activeFamilyId)) {
      handleSearch()
    }
  }, [activeFamilyId, familyResearchStatus, hasSearched, handleSearch])

  // Initial load: run the program-level ("All") search once so the view isn't
  // stuck on the empty state. The family-change effect above deliberately no-ops
  // on its first run, and re-clicking the already-selected "All" pill is a no-op,
  // so without this nothing would ever search on first open.
  const didInitialSearchRef = useRef(false)
  useEffect(() => {
    if (didInitialSearchRef.current || !programId) return
    didInitialSearchRef.current = true
    handleSearch()
  }, [programId, handleSearch])

  const handleSortChange = (newSort: SortOption) => {
    trackEvent('filter_applied', { program_id: programId, filter_type: 'sort', filter_value: newSort })
    setSortBy(newSort)
    setShowSortDropdown(false)
    // Pass newSort explicitly — setSortBy hasn't updated the handleSearch
    // closure yet, so a bare handleSearch() would search by the previous sort.
    if (hasSearched) handleSearch(newSort)
  }

  const handleFamilyChange = useCallback((familyId: string | null) => {
    // Validate that the family exists in acceptedFamilies before setting it
    if (familyId !== null && !acceptedFamilies.some(f => f.id === familyId)) {
      console.warn(`Family ${familyId} not found in accepted families, resetting to All`)
      setActiveFamilyId(null)
      return
    }

    setActiveFamilyId(familyId)
    // The program-level Basic Requirements sort isn't offered in family view
    if (familyId && sortBy === 'need_fit') {
      setSortBy('overall')
    }
  }, [sortBy, acceptedFamilies])

  // Load more results (infinite scroll)
  const loadMore = useCallback(async () => {
    if (!searchId || !hasMore || isLoadingMore) return
    setIsLoadingMore(true)
    try {
      const response = await api.marketviewFetchPage({
        search_id: searchId,
        offset: products.length,
      })
      trackEvent('vendors_loaded_more', {
        program_id: programId, offset: products.length, count_loaded: response.products.length,
      })
      setProducts(prev => [...prev, ...response.products])
      setHasMore(response.has_more ?? false)
    } catch {
      setHasMore(false)
    } finally {
      setIsLoadingMore(false)
    }
  }, [searchId, hasMore, isLoadingMore, products.length, programId])

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          loadMore()
        }
      },
      { rootMargin: '200px' }
    )

    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [hasMore, isLoadingMore, loadMore])

  // ── Render ─────────────────────────────────────────────────────

  // Which branch below renders the scrolling result list. Only that branch can
  // host the page chrome, so the flag has to match it exactly — otherwise the
  // toolbar would vanish on the error/loading/empty states.
  const showsScrollingResults =
    !error && !isLoading && products.length > 0 && currentView === 'products'

  // The toolbar and capability filters sit above the scroll container, so they
  // are pinned for the life of the page and scrolling never wins their space
  // back. On a short viewport they move inside it and scroll away with the
  // list; the header's tab row stays pinned, so navigation is never lost
  // (DEV-660). The full-bleed Tradeoffs / Map views do not scroll a list, so
  // their chrome stays pinned at every density.
  const chromeScrollsAway = condensed && showsScrollingResults

  const pageChrome = (
    <>
      {/* Top Bar */}
      <div
        className={`px-4 sm:px-6 ${condensed ? 'py-2' : 'py-3'} border-b border-border flex flex-wrap items-center gap-2 sm:gap-4`}
      >
        {/* Sort */}
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSortDropdown(!showSortDropdown)}
            className="flex items-center gap-1.5 text-xs"
          >
            <ArrowUpDown className="w-3.5 h-3.5" />
            <span className="max-w-[120px] truncate sm:max-w-none">
              {sortOptions.find((o) => o.value === sortBy)?.label}
            </span>
            <ChevronDown className="w-3.5 h-3.5" />
          </Button>
          {showSortDropdown && (
            <div className="absolute left-0 top-full mt-1 w-48 bg-popover border border-border rounded-md shadow-md z-50 overflow-hidden">
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSortChange(option.value)}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-accent
                            ${sortBy === option.value ? 'text-foreground font-medium' : 'text-muted-foreground'}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* View toggle */}
        <ViewToggle currentView={currentView} onChange={setCurrentView} />

        {/* Results count */}
        {hasSearched && (
          <span className="text-sm font-medium whitespace-nowrap ml-auto">
            {isLoading ? (
              <span className="text-muted-foreground">Searching…</span>
            ) : (
              <span className="text-foreground">{products.length} of {totalResults} candidates</span>
            )}
          </span>
        )}
      </div>

      {/* Solution family tabs — shown when families exist */}
      {acceptedFamilies.length > 0 && (
        <div
          className={`px-4 sm:px-6 ${condensed ? 'py-1.5' : 'py-2.5'} border-b border-border flex items-center gap-2 overflow-x-auto flex-shrink-0`}
        >
          <button
            onClick={() => handleFamilyChange(null)}
            className={`px-3 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
              activeFamilyId === null
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            All
          </button>
          {acceptedFamilies.map((family) => {
            const status = familyResearchStatus[family.id]
            const isActive = activeFamilyId === family.id
            const statusLabel =
              status === 'in_progress' ? 'Research in progress'
              : status === 'needs_research' ? 'Research not yet started'
              : status === 'fresh' ? 'Research fresh'
              : undefined
            const tooltip = [family.technology_name, statusLabel].filter(Boolean).join(' — ') || undefined
            return (
              <button
                key={family.id}
                onClick={() => handleFamilyChange(family.id)}
                className={`px-3 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-colors inline-flex items-center gap-1.5 ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
                title={tooltip}
              >
                <span>{family.name}</span>
                {status === 'in_progress' && (
                  <span
                    aria-hidden
                    className="inline-block w-1.5 h-1.5 rounded-full bg-warning animate-pulse"
                  />
                )}
                {status === 'needs_research' && (
                  <span
                    aria-hidden
                    className="inline-block w-1.5 h-1.5 rounded-full bg-destructive"
                  />
                )}
              </button>
            )
          })}
          {/* Research sits next to the status dots that say whether it is
              needed - the shortest explanation of what the button does. */}
          <div className="ml-auto pl-3 shrink-0">{researchButton}</div>
        </div>
      )}

      {researchError && (
        <div className="px-4 sm:px-6 py-2 border-b border-border text-xs text-destructive flex-shrink-0">
          {researchError}
        </div>
      )}

      {/* Research-in-progress banner for the active family — results below may
          be partial until the fan-out completes. Auto-refresh handles fill-in. */}
      {researchRunning && (
        <div
          className="px-4 sm:px-6 py-2 border-b border-border text-xs text-muted-foreground flex items-center gap-2 flex-shrink-0"
          style={{ backgroundColor: 'color-mix(in srgb, var(--muted) 40%, transparent)' }}
        >
          <div className="w-3 h-3 border-2 border-warning border-t-transparent rounded-full animate-spin" />
          <span>
            {activeFamilyId
              ? 'Vendor research is running for this family'
              : 'Vendor research is running'}
            {' - '}
            new results will appear automatically.
            {products.length > 0 &&
              ' The candidates below are live; you can keep shortlisting.'}
          </span>
        </div>
      )}
    </>
  )

  return (
    <div className={
      embedded
        ? 'flex flex-col min-h-0 h-[70vh] border border-border rounded-lg overflow-hidden'
        : 'h-full flex flex-col min-h-0'
    }>
      {!chromeScrollsAway && pageChrome}

      {/* Results Content */}
      {error ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-destructive mb-4">{error}</p>
            <Button onClick={() => handleSearch()}>Retry Search</Button>
          </div>
        </div>
      ) : isLoading || (researchRunning && products.length === 0) ? (
        // Skeletons rather than "No products found" while research is in
        // flight: here we genuinely expect rows, and the empty state would
        // tell the user to start the thing that is already running.
        <div className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-muted-foreground text-sm">
              {!isLoading
                ? 'Vendor research is running. Products appear here as they are found…'
                : activeFamilyId
                  ? 'Finding products in technology family and scoring against scorecards…'
                  : 'Searching and scoring products against program scorecards…'}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5 justify-items-stretch">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="w-full min-w-0 bg-card border border-border rounded-md overflow-hidden animate-pulse"
              >
                <div className="h-40 bg-muted" />
                <div className="p-4 space-y-3">
                  <div className="h-5 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                  <div className="h-16 bg-muted rounded" />
                  <div className="flex justify-center py-4">
                    <div className="w-32 h-32 bg-muted rounded-full" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : products.length === 0 ? (
        <div className="flex-1 flex items-center justify-center p-6">
          <MarketViewEmptyState
            // `families`, not `acceptedFamilies`: the classifier needs the
            // unaccepted ones to tell "go review these" from "run research".
            // Both are SolutionFamily[], so the compiler will not catch a mix-up.
            state={resolveEmptyState(families, activeFamilyId, familiesLoaded)}
            solutionsPath={solutionsPath}
            researchAction={researchButton}
          />
        </div>
      ) : (
        <>
          {currentView === 'products' && (
            <div className="flex-1 overflow-y-auto">
              {chromeScrollsAway && pageChrome}
              <div className="p-4 sm:p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5 justify-items-stretch">
                  {products.map((result, index) => (
                    <div key={result.product.id} className="w-full min-w-0">
                      <ProductResultCard
                        result={result}
                        onClick={() => {
                          setSelectedResult(result)
                          setSelectedResultIndex(index)
                        }}
                        programId={programId}
                        solutionFamilyId={activeFamilyId}
                        onShortlistToggle={(productId, vendorId, isShortlisted) => {
                          setProducts(prev => prev.map(r =>
                            r.product.id === productId && r.vendor.id === vendorId
                              ? { ...r, is_shortlisted: isShortlisted }
                              : r
                          ))
                        }}
                        onShortlistPersisted={onShortlistChange}
                      />
                    </div>
                  ))}
                </div>
                {/* Infinite scroll sentinel */}
                {hasMore && (
                  <div ref={sentinelRef} className="flex justify-center py-8">
                    {isLoadingMore && (
                      <div className="flex items-center gap-3">
                        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm text-muted-foreground">Loading more products…</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {currentView === 'tradeoffs' && (
            <div className="flex-1 relative min-h-0">
              <TradeoffsView results={products} />
            </div>
          )}

          {currentView === 'map' && (
            <div className="flex-1 relative min-h-0">
              <MapView results={products} />
            </div>
          )}
        </>
      )}

      {/* Re-research confirmation. Only reached when products already exist -
          the cost is real (a deep-research pass), so name it, and say plainly
          that nothing is thrown away: the importer is get_or_create + add,
          and nothing on this path deletes a product or a star. */}
      <Modal
        isOpen={confirmResearch}
        onClose={() => setConfirmResearch(false)}
        title={
          activeFamilyId
            ? `Re-research ${researchScope[0]?.name ?? 'this family'}?`
            : 'Re-research every solution family?'
        }
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmResearch(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setConfirmResearch(false)
                runResearch()
              }}
            >
              Re-research
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>
            This runs a fresh deep-research pass over the vendor landscape. It
            takes about 10-20 minutes and runs in the background.
          </p>
          <p>
            Your {researchProductCount} existing{' '}
            {researchProductCount === 1 ? 'product' : 'products'} and your
            shortlist are kept. New vendors are added and scores are refreshed.
          </p>
        </div>
      </Modal>

      {/* Vendor Detail Overlay */}
      {selectedResult && (
        <VendorDetailOverlay
          result={selectedResult}
          programId={programId}
          onClose={() => setSelectedResult(null)}
          onShortlistPersisted={onShortlistChange}
          allResults={products}
          currentIndex={selectedResultIndex}
          onNavigate={(index) => {
            setSelectedResult(products[index])
            setSelectedResultIndex(index)
          }}
        />
      )}
    </div>
  )
}

export default ProgramMarketView
